import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Loader2, Sparkles, Plus, X, Globe, MapPin, Flag
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreateChallenge = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    full_description: '',
    challenge_type: 'regional',
    geographic_scope: '',
    deadline: '',
    difficulty: 'intermediate',
    tags: [],
    criteria: []
  });
  
  const [newTag, setNewTag] = useState('');
  const [newCriteria, setNewCriteria] = useState('');

  const challengeTypes = [
    { value: 'regional', label: 'Regional Impact', icon: MapPin, desc: 'Local community challenge' },
    { value: 'national', label: 'National Impact', icon: Flag, desc: 'Country-wide challenge' },
    { value: 'global', label: 'Global Impact', icon: Globe, desc: 'Worldwide challenge' },
    { value: 'sdg', label: 'SDG Aligned', icon: Sparkles, desc: 'UN Sustainable Development Goals' }
  ];

  const difficultyLevels = [
    { value: 'beginner', label: 'Beginner', color: 'bg-green-100 text-green-700' },
    { value: 'intermediate', label: 'Intermediate', color: 'bg-blue-100 text-blue-700' },
    { value: 'advanced', label: 'Advanced', color: 'bg-purple-100 text-purple-700' }
  ];

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (index) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((_, i) => i !== index)
    });
  };

  const addCriteria = () => {
    if (newCriteria.trim() && !formData.criteria.includes(newCriteria.trim())) {
      setFormData({ ...formData, criteria: [...formData.criteria, newCriteria.trim()] });
      setNewCriteria('');
    }
  };

  const removeCriteria = (index) => {
    setFormData({
      ...formData,
      criteria: formData.criteria.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert('Please log in to create a challenge');
      navigate('/auth');
      return;
    }

    try {
      setLoading(true);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/challenges/create`,
        {
          ...formData,
          color: '#8B5CF6' // Default purple
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        const challengeId = response.data.challenge_id;
        // Redirect to challenge detail page
        navigate(`/challenges/${challengeId}`);
      }
    } catch (error) {
      console.error('Error creating challenge:', error);
      alert(error.response?.data?.detail || 'Failed to create challenge');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/challenges')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Challenges
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Create Challenge
            </h1>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <form onSubmit={handleSubmit}>
          {/* Challenge Type */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Challenge Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {challengeTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.challenge_type === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, challenge_type: type.value })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`} />
                        <span className="font-semibold">{type.label}</span>
                      </div>
                      <p className="text-sm text-gray-600">{type.desc}</p>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Challenge Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Challenge Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Sustainable Urban Transportation Solutions"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Short Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief overview (2-3 sentences)"
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="full_description">Full Description *</Label>
                <Textarea
                  id="full_description"
                  value={formData.full_description}
                  onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                  placeholder="Detailed problem statement, context, and desired outcomes"
                  rows={6}
                  required
                />
              </div>

              <div>
                <Label htmlFor="geographic_scope">Geographic Scope</Label>
                <Input
                  id="geographic_scope"
                  value={formData.geographic_scope}
                  onChange={(e) => setFormData({ ...formData, geographic_scope: e.target.value })}
                  placeholder="e.g., San Francisco Bay Area, California, USA"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Specify city, state, country, or leave blank for global</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="deadline">Deadline *</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label>Difficulty Level</Label>
                  <div className="flex gap-2 mt-1">
                    {difficultyLevels.map((level) => (
                      <button
                        key={level.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, difficulty: level.value })}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          formData.difficulty === level.value
                            ? level.color + ' border-2 border-current'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {level.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Tags & Criteria</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="e.g., Climate, Technology, Education"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" onClick={addTag} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      {tag}
                      <button type="button" onClick={() => removeTag(index)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <Label>Evaluation Criteria</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newCriteria}
                    onChange={(e) => setNewCriteria(e.target.value)}
                    placeholder="e.g., Innovation, Feasibility, Impact"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCriteria())}
                  />
                  <Button type="button" onClick={addCriteria} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.criteria.map((criteria, index) => (
                    <span key={index} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                      {criteria}
                      <button type="button" onClick={() => removeCriteria(index)}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-lg py-6"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Creating Challenge...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Create Challenge
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChallenge;
